import { useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useViewNavigate } from '../../hooks/useViewNavigate.js';
import { useAuth } from '../../auth/AuthProvider.js';
import { requestOtp, AuthError } from '../../lib/authApi.js';
import {
  AuthBrandRow,
  AuthField,
  AuthIconArrow,
  AuthIconMail,
  AuthSocialRow,
  isValidEmail,
  takeWebOauthProvider,
} from './AuthPieces.js';
import { AuthLayout } from './AuthLayout.js';
import { extractOtp } from './extractOtp.js';
import { socialLoginErrorMessage } from './socialLoginCopy.js';
import { publicPageUrl } from '../../lib/legalLinks.js';
import { track } from '../../lib/analytics.js';
import './auth.css';

const RESEND_COOLDOWN_S = 30;
const CODE_LENGTH = 6;

/** ¿Podemos leer el portapapeles? (necesita contexto seguro: https o localhost) */
const CAN_READ_CLIPBOARD =
  typeof navigator !== 'undefined' && typeof navigator.clipboard?.readText === 'function';

/**
 * Entrada única de speak: passwordless por código OTP (estilo Anthropic).
 * El mismo flujo registra Y loguea — no hay pantallas de password. Dos pasos:
 *   1. email → `POST /api/auth/otp/request` (siempre ok, anti-enum).
 *   2. código de 6 dígitos → `otpVerify()` (setea el user; crea la cuenta si
 *      el email es nuevo y devuelve `isNewUser` → onboarding /welcome).
 * Mobile-first: el usuario no sale de la app. Reenvío con cooldown.
 */
export function OtpLoginScreen() {
  const navigate = useViewNavigate();
  const { otpVerify } = useAuth();

  const [step, setStep] = useState<'email' | 'code'>('email');
  const [email, setEmail] = useState('');
  const [code, setCode] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [cooldown, setCooldown] = useState(0);
  const codeRef = useRef<HTMLInputElement>(null);
  const [searchParams, setSearchParams] = useSearchParams();

  // Web Google / Apple callbacks come back as `/login?error=oauth_<code>`
  // (legacy Google: `?error=oauth`). Show its copy once and drop the param, so a
  // reload or a back does not bring the banner back.
  const oauthError = searchParams.get('error');
  useEffect(() => {
    if (!oauthError?.startsWith('oauth')) return;
    setError(socialLoginErrorMessage(oauthError, takeWebOauthProvider(), 'web'));
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        next.delete('error');
        return next;
      },
      { replace: true },
    );
  }, [oauthError, setSearchParams]);

  // Tick del cooldown de reenvío.
  useEffect(() => {
    if (cooldown <= 0) return;
    const t = setTimeout(() => setCooldown((s) => s - 1), 1000);
    return () => clearTimeout(t);
  }, [cooldown]);

  // Auto-focus del input de código al pasar al paso 2.
  useEffect(() => {
    if (step === 'code') codeRef.current?.focus();
  }, [step]);

  const sendCode = async () => {
    if (!isValidEmail(email) || submitting) return;
    setSubmitting(true);
    setError(null);
    try {
      await requestOtp(email.trim());
      setStep('code');
      setCooldown(RESEND_COOLDOWN_S);
    } catch (err) {
      setError(err instanceof AuthError ? err.message : 'Algo salió mal. Probá de nuevo.');
    } finally {
      setSubmitting(false);
    }
  };

  const verify = async (value: string) => {
    if (value.length !== CODE_LENGTH || submitting) return;
    setSubmitting(true);
    setError(null);
    try {
      // A web Google/Apple redirect abandoned earlier must not be read as this sign-in's
      // method by AnalyticsBridge.
      takeWebOauthProvider();
      const { isNewUser } = await otpVerify(email.trim(), value);
      track('sign_in_completed', { method: 'otp', new_user: isNewUser });
      // Cuenta nueva → onboarding; sesión existente → home.
      void navigate(isNewUser ? '/welcome' : '/home', { replace: true });
    } catch (err) {
      setError(err instanceof AuthError ? err.message : 'Algo salió mal. Probá de nuevo.');
      setCode('');
    } finally {
      setSubmitting(false);
    }
  };

  /**
   * Un toque para traer el código del portapapeles. En Android NINGÚN teclado
   * ofrece el código que llegó por EMAIL (el autofill nativo de Android es sólo
   * para SMS, vía WebOTP), así que el camino corto real es: copiarlo del mail y
   * tocar acá. En iOS 17+ el AutoFill de Mail ya lo sugiere solo sobre el input
   * `autocomplete="one-time-code"` — el botón queda como red de contención.
   */
  const pasteCode = async () => {
    setError(null);
    // El try envuelve SÓLO la lectura del portapapeles (el permiso lo puede
    // negar el usuario o el navegador): un error del verify de abajo tiene su
    // propio mensaje y no hay que taparlo con "no pudimos leer el portapapeles".
    let clipboard: string;
    try {
      clipboard = await navigator.clipboard.readText();
    } catch {
      setError('No pudimos leer el portapapeles. Pegá el código a mano.');
      return;
    }
    const found = extractOtp(clipboard);
    if (!found) {
      setError('No encontramos un código de 6 dígitos en lo que copiaste.');
      return;
    }
    setCode(found);
    await verify(found);
  };

  const onCodeChange = (raw: string) => {
    const digits = raw.replace(/\D/g, '').slice(0, CODE_LENGTH);
    setCode(digits);
    if (digits.length === CODE_LENGTH) void verify(digits);
  };

  return (
    <AuthLayout>
      <AuthBrandRow />

      <div className="auth-greet">
        <span className="tag">CÓDIGO POR EMAIL</span>
        <h1>{step === 'email' ? 'Entrá con tu email' : 'Revisá tu email'}</h1>
        <p>
          {step === 'email'
            ? '// Sin contraseñas: te mandamos un código de 6 dígitos. Si es tu primera vez, creamos tu cuenta con el mismo paso.'
            : `// Escribí el código que enviamos a ${email}. Vence en 10 minutos.`}
        </p>
      </div>

      <div className="auth-form">
        {error && <div className="auth-banner is-error">{error}</div>}

        {step === 'email' ? (
          <>
            <AuthField
              label="Email"
              icon={<AuthIconMail />}
              type="email"
              value={email}
              onChange={setEmail}
              placeholder="vos@email.com"
              autoComplete="email"
              onEnter={() => void sendCode()}
            />
            <button
              type="button"
              className="auth-submit"
              disabled={!isValidEmail(email) || submitting}
              onClick={() => void sendCode()}
            >
              <span>{submitting ? 'Enviando…' : 'Continuar'}</span>
              <span className="arrow">
                <AuthIconArrow />
              </span>
            </button>
            {/* Google / Apple (native in the app, redirect on the web) — alternative to the code. */}
            <AuthSocialRow onError={setError} disabled={submitting} />
          </>
        ) : (
          <>
            <div className="auth-field">
              <div className="auth-field-label">
                <span>Código</span>
                {CAN_READ_CLIPBOARD ? (
                  <button
                    type="button"
                    className="auth-link auth-paste"
                    disabled={submitting}
                    onClick={() => void pasteCode()}
                  >
                    Pegar código
                  </button>
                ) : (
                  <span className="hint">6 dígitos</span>
                )}
              </div>
              {/* Sin `maxLength`: el navegador RECORTA lo pegado al límite ANTES
                  del onChange, así que el "662 663" que queda al copiar del email
                  (agrupado 3+3) llegaba como "662 66" y el filtro de dígitos lo
                  dejaba en 5 — el user tenía que tipear el último a mano. El tope
                  de 6 lo pone `onCodeChange`, que primero saca lo que no es dígito. */}
              <input
                ref={codeRef}
                className="auth-otp-input"
                value={code}
                onChange={(e) => onCodeChange(e.target.value)}
                inputMode="numeric"
                autoComplete="one-time-code"
                placeholder="••••••"
                data-zbfr-redact
              />
            </div>
            <button
              type="button"
              className="auth-submit"
              disabled={code.length !== CODE_LENGTH || submitting}
              onClick={() => void verify(code)}
            >
              <span>{submitting ? 'Verificando…' : 'Continuar'}</span>
              <span className="arrow">
                <AuthIconArrow />
              </span>
            </button>
            <div className="auth-row-between">
              <button
                type="button"
                className="auth-link"
                disabled={cooldown > 0 || submitting}
                onClick={() => void sendCode()}
                style={{ background: 'none', border: 'none', cursor: cooldown > 0 ? 'default' : 'pointer' }}
              >
                {cooldown > 0 ? `Reenviar en ${cooldown}s` : 'Reenviar código'}
              </button>
              <button
                type="button"
                className="auth-link"
                disabled={submitting}
                onClick={() => {
                  setStep('email');
                  setCode('');
                  setError(null);
                }}
                style={{ background: 'none', border: 'none', cursor: 'pointer' }}
              >
                Cambiar email
              </button>
            </div>
          </>
        )}
      </div>

      <div className="auth-foot">
        <span style={{ fontSize: 12, lineHeight: 1.5 }}>
          Al continuar aceptás los{' '}
          <a
            href={publicPageUrl('/legal/terminos.html')}
            target="_blank"
            rel="noopener noreferrer"
            style={{ color: 'var(--text)', textDecoration: 'underline', textUnderlineOffset: 3 }}
          >
            Términos
          </a>{' '}
          y la{' '}
          <a
            href={publicPageUrl('/legal/privacidad.html')}
            target="_blank"
            rel="noopener noreferrer"
            style={{ color: 'var(--text)', textDecoration: 'underline', textUnderlineOffset: 3 }}
          >
            Política de privacidad
          </a>
          .
        </span>
      </div>
    </AuthLayout>
  );
}
